import { RtApraoHttpErrorData } from "@aprao/common/src/runtypes/common.js";
import { css } from "@emotion/react";
import { HTTPError, TimeoutError } from "ky";
import type { ElementType } from "react";

import { isJsonHttpError, RTFailedFetchError } from "../../common/kyUtils.js";
import { emailAddressSupport } from "../../i18n/constants.js";
import type { UiStateStore } from "./UiStateStore.js";

interface PhaseConf {
  /**
   * The place in the app where the error will be shown.
   */
  context:
    | "main"
    | "rootBanner"
    | "projectsPopover"
    | "shareDialog"
    | "sharingBanner";
}

interface MessageConf {
  description: ElementType<unknown>;
  actions?: Array<{
    label: string;
    handler: (uiState: UiStateStore) => void;
  }>;
}

const inferErrorsPhases = <TName extends string>(
  phases: Record<
    TName,
    PhaseConf & {
      messages?: Record<string, MessageConf>;
    }
  >,
) => phases;

/**
 * A collection of default messages for the "known" errors.
 *
 * These can be overridden per phase if they are too generic.
 */
const defaultErrorsMessages: Record<string, MessageConf> = {
  "404": {
    description: () => (
      <>
        It looks like it doesn't exist anymore. You will find the list of your
        current projects in your boards. If you think there is something wrong,
        you can{" "}
        <a
          css={css`
            color: inherit;
          `}
          href={`mailto:${emailAddressSupport}`}
        >
          contact us
        </a>
        .
      </>
    ),
  },
  "401": {
    description: () => (
      <>
        You have been logged out or your session has expired. Please sign in
        again to continue to work with Aprao.
      </>
    ),
    actions: [
      {
        label: "Sign in",
        handler: (uiState) => uiState.signIn(),
      },
    ],
  },
  VersionMismatch: {
    description: () => (
      <>
        While you were away, we upgraded Aprao. As a result, auto-saving doesn't
        work anymore. Please reload the page to get the new version and benefit
        from the improvements we made.
      </>
    ),
    actions: [
      {
        label: "Reload",
        handler: (uiState) => uiState.reload(),
      },
    ],
  },
  ConcurrentUpdateConflict: {
    description: () => (
      <>
        It seems your data was modified from another place while you were on
        this screen. Please reload the page to get the last version, and please
        re-type your changes.
      </>
    ),
    actions: [
      {
        label: "Reload",
        handler: (uiState) => uiState.reload(),
      },
    ],
  },
  FailedToFetch: {
    description: () => (
      <>
        Looks like we can not reach the server. Please check your internet
        connection and try again.
      </>
    ),
  },
  Timeout: {
    description: () => (
      <>
        Looks like the server is taking to long to respond. Please check your
        internet connection or try again in a while.
      </>
    ),
  },
};

const autoSaveConcurrentUpdateConflict: MessageConf = {
  description: () => (
    <>
      We stopped auto-save. It seems your data was modified from another place
      while you were on this screen. To be safe and prevent overwriting, we
      stopped auto-save. Please reload the page to get the last version, and
      please re-type your changes.
    </>
  ),
  actions: [
    {
      label: "Reload",
      handler: (uiState) => uiState.reload(),
    },
  ],
};

/**
 * The error configuration for each "phase".
 *
 * It defines the heading and can also contain the messages overrides if it
 * needs to be refined.
 */

const phasesConf = inferErrorsPhases({
  global: { context: "rootBanner" },
  globalRefresh: { context: "rootBanner" },
  appraisalCopy: { context: "rootBanner" },
  appraisalCreate: { context: "main" },
  appraisalDelete: { context: "rootBanner" },
  appraisalLoad: { context: "main" },
  appraisalReportGenerate: { context: "rootBanner" },
  appraisalSave: {
    context: "rootBanner",
    messages: {
      ConcurrentUpdateConflict: autoSaveConcurrentUpdateConflict,
    },
  },
  appraisalShare: { context: "shareDialog" },
  appraisalSharingLoad: {
    context: "sharingBanner",
    messages: {
      "403": {
        description: () => (
          <>
            The person who shared the link with you has decided to turn off
            sharing or cancel their subscription.
          </>
        ),
      },
      "404": {
        description: () => <>It looks like it doesn't exist anymore.</>,
      },
    },
  },
  boardChange: { context: "rootBanner" },
  boardCreate: { context: "rootBanner" },
  boardDelete: { context: "rootBanner" },
  boardListAppend: { context: "rootBanner" },
  boardListChange: { context: "rootBanner" },
  boardListDelete: { context: "rootBanner" },
  boardLoad: { context: "main" },
  boardsHomeLoad: { context: "main" },
  cashflowDownload: { context: "rootBanner" },
  forecastDownload: { context: "rootBanner" },
  projectArchive: { context: "rootBanner" },
  projectArchivesLoad: { context: "projectsPopover" },
  projectCreate: { context: "main" },
  projectCreateFromPublicTemplate: { context: "rootBanner" },
  projectCreateFromSharingLink: { context: "main" },
  projectCreateFromTemplate: { context: "projectsPopover" },
  projectDelete: { context: "projectsPopover" },
  projectMakeTemplate: { context: "rootBanner" },
  projectMove: { context: "rootBanner" },
  projectPublicTemplateLoad: {
    context: "main",
  },
  projectSave: {
    context: "rootBanner",
    messages: {
      ConcurrentUpdateConflict: autoSaveConcurrentUpdateConflict,
    },
  },
  projectSend: { context: "rootBanner" },
  projectTemplateToggleVisibility: { context: "projectsPopover" },
  projectTemplatesLoad: { context: "projectsPopover" },
  projectUnarchive: { context: "projectsPopover" },
});

export type PhasesNames = keyof typeof phasesConf;

export interface ErrorInfo {
  phase: PhasesNames;
  name: string;
  url?: string;
  knownError: boolean;
}

export type ErrorConf = ErrorInfo & PhaseConf & Partial<MessageConf>;

export const defaultsConf = {
  heading: "Sorry, something went wrong.",
  description: () => (
    <>
      We are now aware of the problem. Thanks for bearing with us while we are
      fixing it. Sometimes it also helps to refresh the page and try again.
    </>
  ),
} as const;

/**
 * Returns a collection of error names ordered by explicitness. The first one
 * is the best description for the error.
 */
const findErrorNames = async (
  cause: unknown,
): Promise<ReadonlyArray<string>> => {
  if (cause instanceof HTTPError) {
    const errorStatus = cause.response.status.toString();

    // if we have more precise error data, we overwrite it
    if (isJsonHttpError(cause)) {
      const parsedJson = RtApraoHttpErrorData.safeParse(
        await cause.response.clone().json(),
      );

      if (parsedJson.success) {
        return [parsedJson.data.name, errorStatus];
      }
    }

    return [errorStatus];
  } else if (cause instanceof TimeoutError) {
    return ["Timeout"];
  } else if (RTFailedFetchError.safeParse(cause).success) {
    return ["FailedToFetch"];
  } else {
    return ["Unknown"];
  }
};

export const buildErrorInfo = async (
  phase: PhasesNames,
  cause?: unknown,
): Promise<ErrorInfo> => {
  const errorName = (await findErrorNames(cause)).find(
    (name) =>
      phasesConf[phase].messages?.[name] != null ||
      defaultErrorsMessages[name] != null,
  );

  const isErrorKnown = errorName != null;

  return {
    name: isErrorKnown ? errorName : "Unknown",
    knownError: isErrorKnown,
    url: cause instanceof HTTPError ? cause.response.url : undefined,
    phase,
  };
};

export const buildErrorConf = (errorInfo: ErrorInfo): ErrorConf => {
  const phaseConf = phasesConf[errorInfo.phase];

  const message =
    phaseConf.messages?.[errorInfo.name] ??
    defaultErrorsMessages[errorInfo.name] ??
    null;

  return {
    ...errorInfo,
    context: phaseConf.context,
    knownError: message != null,
    ...(message ?? {
      description: defaultsConf.description,
    }),
  };
};
